/**
 * Where an AdVenture Plus signup came from (2026-10-16, paid-media test).
 *
 * The browser keeps the last tagged touch (client/src/lib/lead-attribution.ts,
 * rememberPaidTouch) and sends it with POST /api/pro/checkout. The server
 * cleans it here, writes it to the Stripe Checkout Session's metadata, and
 * completeProCheckout reads it back from that metadata onto
 * pro_subscriptions.attribution, so it survives the trip through Stripe.
 *
 * Shared because the admin table labels rows with the same module. Pure
 * functions only: nothing here may touch the network or storage.
 */

export type PlusAttribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  /** Path the visitor landed on for this touch, no query string. */
  landing_path?: string;
  /** External referrer, origin + path only (no query string). */
  referrer?: string;
  /** ISO time the touch was recorded. */
  touched_at?: string;
};

/** Field caps. Stripe metadata values max out at 500 characters. */
const LIMITS: Record<keyof PlusAttribution, number> = {
  utm_source: 200,
  utm_medium: 200,
  utm_campaign: 200,
  utm_content: 200,
  utm_term: 200,
  gclid: 500,
  gbraid: 500,
  wbraid: 500,
  landing_path: 300,
  referrer: 500,
  touched_at: 40,
};

export const PLUS_ATTRIBUTION_KEYS = Object.keys(LIMITS) as (keyof PlusAttribution)[];

/** The fields that make a touch "tagged": a later untagged visit never replaces it. */
export const TAGGED_KEYS: readonly (keyof PlusAttribution)[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
];

export function isTaggedTouch(a: PlusAttribution | null | undefined): boolean {
  return !!a && TAGGED_KEYS.some((k) => !!a[k]);
}

/**
 * Client-supplied and unauthenticated: keep known keys, strings only, trimmed
 * and capped. Returns null when nothing usable is left.
 */
export function sanitizePlusAttribution(raw: unknown): PlusAttribution | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const src = raw as Record<string, unknown>;
  const out: PlusAttribution = {};
  for (const key of PLUS_ATTRIBUTION_KEYS) {
    const v = src[key];
    if (typeof v !== "string") continue;
    const t = v.trim().slice(0, LIMITS[key]);
    if (t) out[key] = t;
  }
  if (out.touched_at && Number.isNaN(Date.parse(out.touched_at))) delete out.touched_at;
  return Object.keys(out).length ? out : null;
}

/** Stripe Checkout Session metadata entries (11 keys at most, all within Stripe's limits). */
export function plusAttributionMetadata(a: PlusAttribution | null): Record<string, string> {
  if (!a) return {};
  const out: Record<string, string> = {};
  for (const key of PLUS_ATTRIBUTION_KEYS) {
    const v = a[key];
    if (v) out[key] = v;
  }
  return out;
}

/** Read the attribution back off a session's metadata (same keys, re-cleaned). */
export function plusAttributionFromMetadata(metadata: Record<string, string> | null | undefined): PlusAttribution | null {
  return metadata ? sanitizePlusAttribution(metadata) : null;
}

function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * One short line for the admin table: "youtube / paid · plus-dg-us", "gclid",
 * "google.com", "direct". Null when the row has no attribution at all (every
 * member who joined before this shipped).
 */
export function plusAttributionLabel(a: PlusAttribution | null | undefined): string | null {
  if (!a) return null;
  const sourceMedium = [a.utm_source, a.utm_medium].filter(Boolean).join(" / ");
  if (sourceMedium || a.utm_campaign) {
    return [sourceMedium, a.utm_campaign].filter(Boolean).join(" · ");
  }
  if (a.gclid) return "gclid";
  if (a.gbraid) return "gbraid";
  if (a.wbraid) return "wbraid";
  if (a.utm_content || a.utm_term) return [a.utm_content, a.utm_term].filter(Boolean).join(" · ");
  if (a.referrer) return hostOf(a.referrer);
  return "direct";
}
